import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AppLayout, RequireAuth } from "~/layouts/AppLayout";
import { AuthLayout } from "~/layouts/AuthLayout";
import { useIsAuthed } from "~/stores/authStore";
import { useUiStore } from "~/stores/uiStore";
import { Skeleton } from "~/components/base/Skeleton";
// Auth pages stay eager — they are the entry point for unauthed users.
import Login from "~/pages/Login";
import Register from "~/pages/Register";
import NotFound from "~/pages/NotFound";

// Protected pages are lazy-loaded so the initial bundle stays slim.
const Dashboard = lazy(() => import("~/pages/Dashboard"));
const Catalog = lazy(() => import("~/pages/Catalog"));
const Cart = lazy(() => import("~/pages/Cart"));
const Orders = lazy(() => import("~/pages/Orders"));
const OrderDetail = lazy(() => import("~/pages/OrderDetail"));
const Invoices = lazy(() => import("~/pages/Invoices"));
const Account = lazy(() => import("~/pages/Account"));

function ApplyDocumentAttrs() {
  const theme = useUiStore((s) => s.theme);
  const density = useUiStore((s) => s.density);
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    document.documentElement.setAttribute("data-density", density);
  }, [theme, density]);
  return null;
}

function RootRedirect() {
  const isAuthed = useIsAuthed();
  return <Navigate to={isAuthed ? "/dashboard" : "/login"} replace />;
}

function PageFallback() {
  return (
    <div className="page" data-screen-label="loading">
      <Skeleton height={32} width={240} />
      <div style={{ marginTop: 16 }}>
        <Skeleton height={200} />
      </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ApplyDocumentAttrs />
      <Routes>
        <Route path="/" element={<RootRedirect />} />

        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
        </Route>

        <Route
          element={
            <RequireAuth>
              <AppLayout />
            </RequireAuth>
          }
        >
          <Route
            path="/dashboard"
            element={
              <Suspense fallback={<PageFallback />}>
                <Dashboard />
              </Suspense>
            }
          />
          <Route
            path="/catalog"
            element={
              <Suspense fallback={<PageFallback />}>
                <Catalog />
              </Suspense>
            }
          />
          <Route
            path="/cart"
            element={
              <Suspense fallback={<PageFallback />}>
                <Cart />
              </Suspense>
            }
          />
          <Route
            path="/orders"
            element={
              <Suspense fallback={<PageFallback />}>
                <Orders />
              </Suspense>
            }
          >
            <Route
              path=":id"
              element={
                <Suspense fallback={<PageFallback />}>
                  <OrderDetail />
                </Suspense>
              }
            />
          </Route>
          <Route
            path="/invoices"
            element={
              <Suspense fallback={<PageFallback />}>
                <Invoices />
              </Suspense>
            }
          />
          <Route
            path="/account"
            element={
              <Suspense fallback={<PageFallback />}>
                <Account />
              </Suspense>
            }
          />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}
